import { z } from 'zod';
import { isSmartSearchTag } from './smart-search-tags';

export const ParsedHomeTypeSchema = z.object({
  house: z.boolean(),
  condo: z.boolean(),
  townhouse: z.boolean(),
  farm: z.boolean(),
  land: z.boolean(),
  commercial: z.boolean(),
});

export const ParsedLockerSchema = z.enum(['yes', 'no', 'any']);

export const ParsedSearchFiltersSchema = z.object({
  country: z.string().optional(),
  province: z.string().optional(),
  city: z.string().optional(),
  neighbourhood: z.string().optional(),
  homeType: ParsedHomeTypeSchema.optional(),
  bedrooms: z.string().optional(),
  bathrooms: z.string().optional(),
  parkingSpaces: z.string().optional(),
  sqftMin: z.number().nonnegative().optional(),
  sqftMax: z.number().nonnegative().optional(),
  priceMin: z.number().nonnegative().optional(),
  priceMax: z.number().nonnegative().optional(),
  garage: z.boolean().optional(),
  pool: z.boolean().optional(),
  fireplace: z.boolean().optional(),
  waterfront: z.boolean().optional(),
  openHouse: z.boolean().optional(),
  rental: z.boolean().optional(),
  status: z.string().optional(),
  sort: z.string().optional(),
  listedSince: z.string().optional(),
  listedTo: z.string().optional(),
  hasImage: z.boolean().optional(),
  showComingSoon: z.boolean().optional(),
  additional: z
    .object({
      condoOrTownhouse: z
        .object({
          locker: ParsedLockerSchema.optional(),
          maintenanceFee: z.number().nonnegative().optional(),
        })
        .optional(),
    })
    .optional(),
});

export const MatchSpanSchema = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
  field: z.string().min(1),
  raw: z.string(),
  value: z.unknown(),
  confidence: z.number().min(0).max(1).optional(),
  ruleId: z.string().optional(),
});

export const ParserPipelineFlagsSchema = z.object({
  extractFilters: z.boolean(),
  sortExtraction: z.boolean(),
  relativeDate: z.boolean(),
  locationIdentification: z.boolean(),
  googlePlacePredictions: z.boolean(),
  confidence: z.boolean(),
  generateTags: z.boolean(),
  aiFallback: z.boolean(),
  fuzzyMatch: z.boolean(),
});

export const ParseMetricsSchema = z.object({
  query: z.string(),
  normalizedQuery: z.string(),
  matchCoverage: z.number().min(0).max(1),
  residualRate: z.number().min(0).max(1),
  durationMs: z.number().nonnegative(),
  // Zod 4: use explicit union so the key stays required (T | null), not optional.
  confidence: z.union([z.number().min(0).max(1), z.null()]),
  matchedFields: z.array(z.string()),
  residual: z.union([z.string(), z.null()]),
  flags: ParserPipelineFlagsSchema,
});

export const SmartSearchTagSchema = z.string().refine(isSmartSearchTag, {
  message: 'Unknown smart-search tag',
});

export const ParseNaturalLanguageFiltersResultSchema = z.object({
  filters: ParsedSearchFiltersSchema,
  tags: z.union([z.array(SmartSearchTagSchema), z.null()]),
  spans: z.array(MatchSpanSchema),
  unparsed: z.union([z.string(), z.null()]),
  metrics: ParseMetricsSchema,
});

const SmartSearchLlmResponseBaseSchema = z.object({
  filters: ParsedSearchFiltersSchema,
  confidence: z.number().min(0).max(1),
  unparsed: z.union([z.string(), z.null()]),
});

/** LLM JSON when `generateTags` is off — no `tags` field. */
export const SmartSearchLlmResponseSchema = SmartSearchLlmResponseBaseSchema;

/** LLM JSON when `generateTags` is on — `tags` is required (may be empty). */
export const SmartSearchLlmResponseWithTagsSchema = SmartSearchLlmResponseBaseSchema.extend({
  tags: z.array(SmartSearchTagSchema),
});

/**
 * Picks the schema matching the `generateTags` flag. Overloaded so a literal
 * flag yields the precise schema; a runtime boolean yields the union, and the
 * caller must treat `tags` as optional.
 */
/* eslint-disable no-redeclare -- TypeScript overload signatures, not redeclarations */
export function smartSearchLlmResponseSchema(
  generateTags: true
): typeof SmartSearchLlmResponseWithTagsSchema;
export function smartSearchLlmResponseSchema(
  generateTags: false
): typeof SmartSearchLlmResponseSchema;
export function smartSearchLlmResponseSchema(
  generateTags: boolean
): typeof SmartSearchLlmResponseWithTagsSchema | typeof SmartSearchLlmResponseSchema;
export function smartSearchLlmResponseSchema(generateTags: boolean) {
  return generateTags ? SmartSearchLlmResponseWithTagsSchema : SmartSearchLlmResponseSchema;
}
/* eslint-enable no-redeclare */
